'use client';

import {
  type CtaHeaderProps,
  type FooterV4Props,
  type HeaderV4Props,
  type LayoutProps,
  PageLayoutV4 as Layout,
  type PageLayoutV4Props,
} from '@jouzen/ecom-components/layout';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import usePageView from '@/analytics/hooks/usePageView';
import { usePartnerCode } from '@/app/hooks/usePartnerCode';
import usePartnerUtm from '@/app/hooks/usePartnerUtm/usePartnerUtm';

import MultivariatePaymentOptions from '../MultivariatePaymentOptions';
import eyebrowMessage from './_components/eyebrowMessage';
import usePageLayout from './_hooks/usePageLayout';

export interface PageLayoutProps
  extends Omit<
    LayoutProps,
    | 'mainLabel'
    | 'footerProps'
    | 'headerProps'
    | 'eyebrowProps'
    | 'footnotesProps'
  > {
  readonly ctaHeaderProps?: CtaHeaderProps;
  readonly pageName: string;
  readonly footerProps?: Partial<FooterV4Props>;
  readonly footnotesProps?: PageLayoutV4Props['footnotesProps'];
  readonly headerProps?: Partial<HeaderV4Props>;
  readonly hideHeader?: boolean;
  readonly preFooterContentProps?:
    | {
        wrapperClassName?: string;
      }
    | undefined;
  readonly enableScrollAnimation?: boolean;
  readonly isCarouselInView?: boolean;
  readonly showCtaHeader?: boolean;
}

const PageLayout = ({
  children,
  className = '',
  ctaHeaderProps: parentCtaHeaderProps,
  pageName,
  containerProps: parentContainerProps,
  footerProps: parentFooterProps,
  footnotesProps,
  headerProps: parentHeaderProps,
  hideHeader = false,
  preFooterContent,
  preFooterContentProps,
  enableScrollAnimation = false,
  isCarouselInView = false,
  showCtaHeader = false,
}: PageLayoutProps) => {
  const t = useTranslations();

  const {
    ctaHeaderProps,
    displayMessage,
    eyebrowConfig,
    eyebrowMessageType,
    footerProps,
    headerProps,
  } = usePageLayout({
    enableScrollAnimation,
    isCarouselInView,
    parentCtaHeaderProps,
    parentFooterProps,
    parentHeaderProps,
  });

  const { h1: heading, ...rest } = parentContainerProps;
  const h1 = heading ? t(heading) : '';

  const containerProps = {
    ...rest,
    h1,
  } as LayoutProps['containerProps'];

  const eyebrowProps = useMemo(
    () =>
      eyebrowMessage({
        displayMessage,
        eyebrowConfig,
        eyebrowMessageType,
      }),
    [displayMessage, eyebrowConfig, eyebrowMessageType],
  );

  // Send analytics page event
  usePageView(pageName);

  // Persist UTM query param data to localStorage (if any params exist)
  usePartnerUtm();

  // Persist partner code query param if exists
  usePartnerCode();

  const _footerProps: FooterV4Props = useMemo(
    () => ({ ...footerProps, multiVariates: <MultivariatePaymentOptions /> }),
    [footerProps],
  );

  return (
    <div data-cy="page-layout-v4">
      <Layout
        className={className}
        containerProps={containerProps}
        eyebrowProps={eyebrowProps}
        footerProps={_footerProps}
        footnotesProps={footnotesProps}
        headerProps={headerProps}
        hideHeader={hideHeader}
        mainLabel="mainLabel"
        preFooterContent={
          <div className={preFooterContentProps?.wrapperClassName}>
            {preFooterContent}
          </div>
        }
        showCtaHeader={showCtaHeader}
        {...ctaHeaderProps}
      >
        {children}
      </Layout>
    </div>
  );
};

export default PageLayout;
